<template>
  <Modal :show="true" @close-modal="$emit('close-modal')">
    <div class="add-member">
      <form
        @submit.prevent="checkFormValidation"
        class="add-member__form"
      >
        <h1 class="add-member__title">
          Create new member
        </h1>
        <p class="add-member__subtitle">
          Provide the user’s details as it appears on their ID.
        </p>
        <div class="add-member__fields">
          <InputField
            class="add-member__field"
            label="First name"
            name="fname"
            @input-changed="storeValue"
            @is-valid="handleValidation"
            :validate="validate"
          />
          <InputField
            class="add-member__field"
            label="Last name"
            name="lname"
            @input-changed="storeValue"
            @is-valid="handleValidation"
            :validate="validate"
          />
          <InputField
            class="add-member__field"
            label="Email"
            name="email"
            type="email"
            @input-changed="storeValue"
            @is-valid="handleValidation"
            :validate="validate"
          />
          <PhoneInput
            class="add-member__field"
            label="Phone"
            name="phone"
            :required="true"
            @phone-input="storeValue"
            @is-valid="handleValidation"
            :validate="validate"
          />
          <div class="add-member__field">
            <InputField
              label="username (optional)"
              name="username"
              :required="false"
              @input-changed="storeValue"
              @is-valid="handleValidation"
              :validate="validate"
            />
            <p v-if="usernameLookupResponse" class="add-member__lookup-msg">
              {{ usernameLookupResponse }}
            </p>
            <p v-if="checkingUsername" class="add-member__lookup-msg">
              Please wait...
            </p>
          </div>
          <OnboardingPasswordInput
            class="add-member__field"
            label="Password"
            name="password"
            border="tw-border-[#DDE1E9]"
            :password="payload.password"
            :error="passwordError || {}"
            inputName="password"
            :disabled="loading"
            @set="storeValue({ value: $event, name: 'password' })"
          />
        </div>
        <div class="add-member__actions">
          <button
            type="button"
            @click="$emit('close-modal')"
            class="add-member__btn add-member__btn--cancel"
          >
            Cancel
          </button>
          <div class="add-member__btn-wrapper" v-if="!checkingUsername">
            <BtnComponent title="Continue" :loading="loading" />
          </div>
        </div>
      </form>
    </div>
  </Modal>
</template>

<script setup>
  import Modal from "@/components/general/Modal";
  import InputField from "../general/InputField.vue";
  import PhoneInput from "../general/PhoneInput.vue";
  import BtnComponent from "../general/BtnComponent.vue";
  import OnboardingPasswordInput from "../onboarding/OnboardingPasswordInput.vue";
  import { useValidation } from "@/composables/formValidation";
  import { computed, reactive, ref, watch } from "vue";
  import { useStore } from "vuex";

  const emit = defineEmits(["close-modal", "success"]);
  const store = useStore();

  const loading = computed(() => store.getters["loading"]);

  const orgDetails = computed(
    () => store.getters["organisationModule/organisationDetails"]
  );

  const {
    fields,
    checkFormValidation,
    handleValidation,
    validate,
    canSubmit,
  } = useValidation();

  const payload = reactive({
    email: "",
    password: "",
    fname: "",
    lname: "",
    username: "",
    phone: "",
    kyc: true,
  });
  const passwordError = ref({});
  const usernameLookupResponse = ref("");
  const usernameValid = ref(false);
  const checkingUsername = ref(false);

  const storeValue = ({ name, value }) => {
    if (name === "phone") {
      let number = `${value.code}${value.number}`;
      number = String(number)
        .split(" ")
        .join("");
      payload.phone = number;
    } else {
      payload[name] = value;
    }
  };
  const addMemberToDealRoom = async () => {
    if (!payload.username) {
      delete payload.username;
    }
    try {
      await store.dispatch("organisationModule/addMemberToDealRoom", {
        payload,
        id: orgDetails.value._id,
      });
      emit("success");
    } catch (error) {
      return error;
    }
  };
  const lookupUsername = async (username) => {
    usernameLookupResponse.value = null;
    checkingUsername.value = true;
    try {
      const response = await store.dispatch(
        "organisationModule/lookupUsername",
        username
      );
      usernameLookupResponse.value = response.data.message;
      usernameValid.value = true;
      checkingUsername.value = false;
    } catch (error) {
      usernameValid.value = false;
      checkingUsername.value = false;
      usernameLookupResponse.value = error.response.data.message;
    }
  };
  watch(validate, (value) => {
    if (value) {
      if (payload.password) {
        handleValidation(true);
      } else {
        passwordError.value = {
          $property: "password",
          $message: "Please enter a valid password",
        };
        handleValidation(false);
      }
    }
  });
  watch(
    () => payload.username,
    (username) => {
      setTimeout(() => {
        if (payload.username === username && username.length > 1) {
          lookupUsername(username);
        }
      }, 1500);
    }
  );
  watch(canSubmit, (value) => {
    if (
      (value && payload.username && usernameValid.value) ||
      (value && !payload.username)
    ) {
      addMemberToDealRoom();
    }
  });
  fields.value = 6;
</script>

<style lang="scss" scoped>
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-subtle: #F8FAFC;
$border-color: #E2E8F0;
$btn-primary: #001240;

.add-member {
  width: 100%;

  @media screen and (min-width: 768px) {
    width: 480px;
  }

  @media screen and (min-width: 1024px) {
    width: 800px;
  }

  &__form {
    width: 100%;
    margin: 0 auto;

    @media screen and (min-width: 768px) {
      max-width: 680px;
    }
  }

  &__title {
    font-size: 24px;
    font-weight: 600;
    color: $text-primary;
    margin-bottom: 8px;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    font-size: 14px;
    color: $text-secondary;
    margin-bottom: 32px;
  }

  &__fields {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;

    @media screen and (min-width: 1024px) {
      gap: 24px;
    }
  }

  &__field {
    width: 100%;

    @media screen and (min-width: 768px) {
      width: 233px;
    }

    @media screen and (min-width: 1024px) {
      width: 100%;
      max-width: 325px;
    }
  }

  &__lookup-msg {
    font-size: 13px;
    color: $text-muted;
    margin-top: 6px;
  }

  &__actions {
    display: flex;
    gap: 12px;
    margin-top: 32px;
  }

  &__btn {
    height: 48px;
    padding: 0 24px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;

    &--cancel {
      background: $bg-subtle;
      color: $text-secondary;
      min-width: 120px;

      &:hover {
        background: darken($bg-subtle, 4%);
      }
    }
  }

  &__btn-wrapper {
    width: 100%;
    max-width: 153px;

    :deep(button) {
      height: 48px;
      border-radius: 10px;
      font-size: 14px;
      font-weight: 500;
    }
  }
}
</style>
